/**
 * The API client.
 *
 * One place that knows the wire shapes, so a route rename breaks the build
 * rather than a screen. Errors come back as a thrown `ApiError` carrying the
 * server's short code — the human wording lives in the UI, next to the button
 * that caused it.
 */

import type { Plan, PlanId } from '../core/plans';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  });

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (!res.ok) {
    const code =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : 'request_failed';
    throw new ApiError(code, res.status);
  }
  return body as T;
}

/* ------------------------------------------------------------------ types */

export interface Account {
  id: string;
  email: string;
  companyName: string;
  trustSlug: string;
  trustPublished: boolean;
  trustIntro: string;
  maxEvidenceAgeDays: number;
  trustTerms: string;
  plan: PlanId;
  /** End of the paid period, epoch ms. `null` on Free. */
  planUntil: number | null;
  planAutoRenew: boolean;
  planStartedAt: number | null;
  /**
   * Who advances the period: 'polar', 'manual', or 'none' on Free.
   *
   * The settings screen branches on this. A Polar subscription is cancelled and
   * re-carded through Polar's own portal, and offering a local cancel button for
   * one would be offering a button that cancels nothing.
   */
  billingProvider: string;
}

export interface TrustDocument {
  id: string;
  title: string;
  description: string;
  filename: string;
  sizeBytes: number;
  published: boolean;
  createdAt: number;
}

export interface AccessRecord {
  documentId: string;
  name: string;
  email: string;
  company: string;
  acceptedAt: number;
  downloadedAt: number | null;
}

/** What is left of each metered limit, as the server computed it. */
export interface LimitCheck {
  allowed: boolean;
  limit: number | null;
  used: number;
  remaining: number | null;
}

export interface Usage {
  period: string;
  evidenceCards: LimitCheck;
  questionnairesPerMonth: LimitCheck;
  draftedAnswersPerMonth: LimitCheck;
  trustDocuments: LimitCheck;
}

/** What the buy button is allowed to promise. See /api/me in the Worker. */
export type CheckoutMode = 'polar' | 'mock' | 'off';

export interface Me {
  account: Account;
  plan: Plan;
  usage: Usage;
  checkout: CheckoutMode;
}

export interface Evidence {
  id: string;
  title: string;
  source: string;
  body: string;
  reviewedAt: number;
  public: boolean;
  stale: boolean;
  ageDays: number;
  daysUntilStale: number;
}

export type AnswerStatus = 'answered' | 'unsupported' | 'unanswered';

export interface Question {
  id: string;
  rowIndex: number;
  text: string;
  status: AnswerStatus;
  answerText: string;
  citations: string[];
  reason: string | null;
}

export interface QuestionnaireSummary {
  id: string;
  customer: string;
  filename: string;
  createdAt: number;
  total: number;
  answered: number;
  unsupported: number;
}

/** What this company told someone the last time the question came up. */
export interface PreviousAnswer {
  customer: string;
  text: string;
  at: number;
}

export interface ImportHint {
  sheetName: string;
  questionColumn: number;
  firstRow: number;
}

export interface Gap {
  key: string;
  text: string;
  askedBy: string[];
  count: number;
  reasons: string[];
}

export interface SettledAnswer {
  questionId: string;
  status: 'answered' | 'unsupported';
  text: string;
  citations: string[];
  reason?: string;
  staleCitations?: string[];
}

export interface TrustPage {
  companyName: string;
  intro: string;
  terms: string;
  evidence: { id: string; title: string; source: string; body: string; reviewedAt: number }[];
  documents: {
    id: string;
    title: string;
    description: string;
    filename: string;
    sizeBytes: number;
  }[];
}

/* ---------------------------------------------------------------- methods */

export const api = {
  me: () => call<Me>('/api/me'),

  signup: (input: { email: string; password: string; companyName: string }) =>
    call<{ account: Account }>('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  login: (input: { email: string; password: string }) =>
    call<{ account: Account }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  logout: () => call<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  updateSettings: (input: Partial<Omit<Account, 'id' | 'email'>>) =>
    call<{ account: Account }>('/api/settings', {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  listEvidence: () => call<{ evidence: Evidence[] }>('/api/evidence'),

  createEvidence: (input: { title: string; source: string; body: string; public: boolean }) =>
    call<{ evidence: Evidence }>('/api/evidence', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  updateEvidence: (
    id: string,
    input: Partial<{ title: string; source: string; body: string; public: boolean }>,
  ) =>
    call<{ evidence: Evidence }>(`/api/evidence/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  reviewEvidence: (id: string) =>
    call<{ evidence: Evidence }>(`/api/evidence/${id}/review`, { method: 'POST' }),

  deleteEvidence: (id: string) => call<{ ok: true }>(`/api/evidence/${id}`, { method: 'DELETE' }),

  listQuestionnaires: () =>
    call<{ questionnaires: QuestionnaireSummary[] }>('/api/questionnaires'),

  createQuestionnaire: (input: {
    customer: string;
    filename: string;
    sheetName: string;
    questionColumn: number;
    firstRow: number;
    questions: { rowIndex: number; text: string }[];
  }) =>
    call<{ id: string; count: number }>('/api/questionnaires', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  getQuestionnaire: (id: string) =>
    call<{
      questionnaire: { id: string; customer: string; filename: string; createdAt: number };
      questions: Question[];
      evidence: Evidence[];
      history: Record<string, PreviousAnswer>;
    }>(`/api/questionnaires/${id}`),

  /** The layout this customer's last file used, if they have sent one before. */
  importHint: (customer: string) =>
    call<{ hint: ImportHint | null }>(`/api/import-hint?customer=${encodeURIComponent(customer)}`),

  deleteQuestionnaire: (id: string) =>
    call<{ ok: true }>(`/api/questionnaires/${id}`, { method: 'DELETE' }),

  draft: (id: string, input: { apiKey: string; questionIds: string[] }) =>
    call<{
      answers: SettledAnswer[];
      summary: { total: number; answered: number; unsupported: number };
    }>(`/api/questionnaires/${id}/draft`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  answerQuestion: (
    questionnaireId: string,
    questionId: string,
    input: { text: string; citations: string[] },
  ) =>
    call<{ answer: SettledAnswer }>(
      `/api/questionnaires/${questionnaireId}/questions/${questionId}`,
      { method: 'PATCH', body: JSON.stringify(input) },
    ),

  gaps: () => call<{ gaps: Gap[] }>('/api/gaps'),

  listDocuments: () =>
    call<{ documents: TrustDocument[]; accessLog: AccessRecord[]; limit: LimitCheck }>(
      '/api/documents',
    ),

  /**
   * Uploaded as multipart rather than JSON, so the file streams to R2 instead of
   * being base64'd through memory on both ends.
   */
  uploadDocument: async (input: { file: File; title: string; description: string }) => {
    const form = new FormData();
    form.append('file', input.file);
    form.append('title', input.title);
    form.append('description', input.description);

    const res = await fetch('/api/documents', { method: 'POST', body: form });
    const body = (await res.json().catch(() => null)) as { error?: string; id?: string } | null;
    if (!res.ok) throw new ApiError(body?.error ?? 'request_failed', res.status);
    return body as { id: string };
  },

  updateDocument: (id: string, input: Partial<Pick<TrustDocument, 'title' | 'description' | 'published'>>) =>
    call<{ ok: true }>(`/api/documents/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),

  deleteDocument: (id: string) => call<{ ok: true }>(`/api/documents/${id}`, { method: 'DELETE' }),

  requestDocumentAccess: (
    slug: string,
    documentId: string,
    input: { name: string; email: string; company: string; accepted: boolean },
  ) =>
    call<{ token: string }>(`/api/trust/${slug}/documents/${documentId}/access`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  documentDownloadUrl: (slug: string, documentId: string, token: string) =>
    `/api/trust/${slug}/documents/${documentId}/download?t=${encodeURIComponent(token)}`,

  trust: (slug: string) => call<TrustPage>(`/api/trust/${slug}`),

  /**
   * Buying a plan.
   *
   * Two shapes come back and the caller has to handle both. Against Polar the
   * answer is a `url` to send the browser to, and the plan itself arrives later
   * by webhook — this call deliberately does not pretend the upgrade is done.
   * Against the mock provider the plan is granted on the spot and the account
   * comes back already changed.
   */
  checkout: (plan: Exclude<PlanId, 'free'>) =>
    call<{ url?: string; account?: Account; mock?: boolean }>('/api/checkout', {
      method: 'POST',
      body: JSON.stringify({ plan }),
    }),

  /**
   * A short-lived link into Polar's customer portal: invoices, receipts and the
   * card on file. None of that is reimplemented here, and none of it should be —
   * reissuing an invoice is the merchant of record's job.
   */
  billingPortal: () => call<{ url: string }>('/api/billing/portal', { method: 'POST' }),

  /** Cancelling leaves the plan running until the period already paid for ends. */
  setAutoRenew: (autoRenew: boolean) =>
    call<{ account: Account }>('/api/subscription', {
      method: 'PATCH',
      body: JSON.stringify({ autoRenew }),
    }),

  /** Always resolves, whether or not the address is on file. See the route. */
  forgotPassword: (email: string) =>
    call<{ ok: true }>('/api/auth/forgot', { method: 'POST', body: JSON.stringify({ email }) }),

  resetPassword: (input: { token: string; password: string }) =>
    call<{ ok: true }>('/api/auth/reset', { method: 'POST', body: JSON.stringify(input) }),
};

/** Human wording for the codes the server returns. */
export function errorMessage(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Something went wrong. Try again.';
  switch (err.code) {
    case 'invalid_credentials':
      return 'That email and password do not match an account.';
    case 'email_taken':
      return 'There is already an account with that email.';
    case 'weak_password':
      return 'Use at least 12 characters.';
    case 'invalid_email':
      return 'That does not look like an email address.';
    case 'invalid_company':
      return 'Enter your company name.';
    case 'slug_taken':
      return 'That trust page address is already taken.';
    case 'too_many_requests':
      return 'Too many attempts. Wait a few minutes.';
    case 'missing_api_key':
      return 'Add your model API key first.';
    case 'provider_rejected_key':
      return 'Your provider rejected that API key.';
    case 'provider_rate_limited':
      return 'Your provider is rate limiting this key. Try again shortly.';
    case 'provider_refused':
      return 'The model declined to answer this batch. Try drafting these questions in smaller groups.';
    case 'provider_unavailable':
      return 'Could not reach the model provider. Nothing was changed.';
    case 'too_many_questions':
      return 'That file has more than 500 questions. Split it and import in parts.';
    case 'no_questions':
      return 'No questions were found in that column.';
    case 'plan_limit_evidence':
      return 'Your plan is out of evidence cards. See Pricing to raise the limit.';
    case 'plan_limit_questionnaires':
      return 'You have used this month\u2019s questionnaires. See Pricing to raise the limit.';
    case 'plan_limit_documents':
      return 'Your plan does not include trust page documents. See Pricing.';
    case 'file_too_large':
      return 'That file is over 25 MB.';
    case 'unsupported_type':
      return 'Only PDF, Word, Excel, PNG, JPEG and plain text can be hosted here.';
    case 'terms_not_accepted':
      return 'You have to accept the terms to download this.';
    case 'invalid_details':
      return 'Enter your name, work email and company.';
    case 'checkout_not_configured':
      return 'Checkout is not configured on this deployment yet.';
    case 'checkout_unavailable':
      return 'We could not reach the payment provider. Nothing was charged \u2014 try again in a moment.';
    case 'billing_unavailable':
      return 'We could not reach the payment provider. Nothing was changed \u2014 try again in a moment.';
    case 'use_billing_portal':
      return 'You already have a subscription. Change your plan from the billing portal in Settings.';
    case 'no_billing_account':
      return 'There is no billing account to open yet. It appears after your first payment.';
    case 'invalid_plan':
      return 'That is not a plan you can buy.';
    case 'already_on_plan':
      return 'You are already on that plan.';
    case 'no_subscription':
      return 'There is no subscription on this account to change.';
    case 'invalid_reset_token':
      return 'That reset link has expired or has already been used. Ask for a new one.';
    case 'plan_limit_drafts':
      return 'You have used this month\u2019s drafted answers. You can still write answers by hand, with a citation.';
    default:
      return 'Something went wrong. Try again.';
  }
}
